import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Layout, Sparkles, Check, Zap, 
  Palette, ArrowRight, Settings, Clock
} from 'lucide-react';

const WebsiteBuilderChoice = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Website Builder
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We offer two ways to create your website. Pick the one that fits your needs best.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Quick Website (Auto Generator) */}
          <Card className="p-8 hover:shadow-xl transition-all border-2 border-emerald-200 bg-emerald-50/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-emerald-600 text-white rounded-lg">
                <Sparkles className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Quick Website</h2>
                <p className="text-emerald-600 font-medium">Auto Generator</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              Perfect for getting online fast! Your website is automatically created from your digital business card in seconds.
            </p>

            <div className="space-y-3 mb-8">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>One-Click Generation</strong> - Ready in seconds</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>No Design Skills</strong> - Fully automated</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Uses Your Business Card</strong> - All data auto-filled</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Optional Enhancements</strong> - Add services, portfolio, hours</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Professional Template</strong> - Beautiful responsive design</span>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>5 seconds</strong> to launch</span>
              </div>
            </div>

            <div className="bg-emerald-100 border border-emerald-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Best for:</strong> Quick launch, business cards, professionals who want to get online fast without technical hassle.
              </p>
            </div>

            <Button
              onClick={() => navigate('/auto-website')}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Create Quick Website
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Card>

          {/* Digi Website (Advanced Builder) */}
          <Card className="p-8 hover:shadow-xl transition-all border-2 border-amber-200 bg-amber-50/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-amber-600 text-white rounded-lg">
                <Layout className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Digi Website</h2>
                <p className="text-amber-600 font-medium">Advanced Builder</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              Full control over your website design! Build custom pages with drag-and-drop sections, choose templates, and customize everything.
            </p>

            <div className="space-y-3 mb-8">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Drag & Drop Builder</strong> - Visual editor</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Multiple Templates</strong> - Choose from 20+ designs</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Section Library</strong> - Hero, About, Gallery, Pricing, etc.</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Full Customization</strong> - Colors, fonts, layouts</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Multiple Pages</strong> - Create unlimited pages</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Your Previous Sites</strong> - All your data is safe here</span>
              </div>
              <div className="flex items-start gap-2">
                <Settings className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>More time</strong> to customize</span>
              </div>
            </div>

            <div className="bg-amber-100 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Best for:</strong> Complete websites, portfolios, e-commerce, restaurants, agencies who need full design control.
              </p>
            </div>

            <Button
              onClick={() => navigate('/website-builder')}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg"
            >
              <Layout className="w-5 h-5 mr-2" />
              Open Advanced Builder
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Card>
        </div>

        {/* Comparison Table */}
        <Card className="p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Feature Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-700 font-semibold">Feature</th>
                  <th className="text-center py-3 px-4 text-emerald-600 font-semibold">Quick Website</th>
                  <th className="text-center py-3 px-4 text-amber-600 font-semibold">Digi Website</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4">Setup Time</td>
                  <td className="text-center py-3 px-4">5 seconds</td>
                  <td className="text-center py-3 px-4">10-30 minutes</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4">Design Skill Required</td>
                  <td className="text-center py-3 px-4">None</td>
                  <td className="text-center py-3 px-4">Basic</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4">Customization Level</td>
                  <td className="text-center py-3 px-4">Limited</td>
                  <td className="text-center py-3 px-4">Full Control</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4">Template Options</td>
                  <td className="text-center py-3 px-4">1 Auto Template</td>
                  <td className="text-center py-3 px-4">20+ Templates</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4">Number of Pages</td>
                  <td className="text-center py-3 px-4">Single Page</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4">Data Source</td>
                  <td className="text-center py-3 px-4">Business Card</td>
                  <td className="text-center py-3 px-4">Manual Input</td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Best Use Case</td>
                  <td className="text-center py-3 px-4">Quick Launch</td>
                  <td className="text-center py-3 px-4">Full Website</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

        {/* Footer Note */}
        <div className="text-center mt-8 text-gray-600">
          <p className="text-sm">
            💡 <strong>Pro Tip:</strong> Start with Quick Website to get online fast, then create a custom site with Digi Website later!
          </p>
        </div>
      </div>
    </div>
  );
};

export default WebsiteBuilderChoice;
